import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('Production');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/api/employees');
      setEmployees(data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      setError('Failed to load employees. Please try again later.');
      setEmployees([]);
    }
    setLoading(false);
  };

  const categories = {
    Production: { label: 'Production', color: '#e94560' },
    'Quality Controllers': { label: 'Quality Controllers', color: '#0f3460' },
    'Project Managers': { label: 'Project Managers', color: '#16213e' },
  };

  const filteredEmployees = employees
    .filter((emp) => emp.category === activeTab)
    .filter((emp) =>
      emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.designation.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const statusColors = {
    Completed: { bg: '#e8f5e9', color: '#2e7d32' },
    Pending: { bg: '#fff3e0', color: '#e65100' },
    Active: { bg: '#e3f2fd', color: '#1565c0' },
    Inactive: { bg: '#fce4ec', color: '#c62828' },
  };
  const getStatusColor = (status) => statusColors[status] || { bg: '#f5f5f5', color: '#666' };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    searchContainer: {
      display: 'flex',
      gap: '12px',
    },
    searchInput: {
      flex: 1,
      padding: '12px 16px',
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      fontSize: '14px',
      transition: 'all 0.3s ease',
    },
    tabContainer: {
      display: 'flex',
      gap: '12px',
      borderBottom: '1px solid #e0e0e0',
      flexWrap: 'wrap',
    },
    tab: {
      padding: '12px 20px',
      border: 'none',
      backgroundColor: 'transparent',
      color: '#999',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      borderBottom: '3px solid transparent',
      marginBottom: '-1px',
    },
    tabActive: {
      color: '#1a1a2e',
      borderBottomColor: '#e94560',
    },
    employeeGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '20px',
    },
    employeeCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      borderTop: '4px solid',
    },
    employeeCardHover: {
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-4px)',
    },
    employeeName: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#1a1a2e',
      marginBottom: '4px',
    },
    employeeDesignation: {
      fontSize: '13px',
      color: '#666',
      marginBottom: '12px',
    },
    infoRow: {
      fontSize: '12px',
      color: '#999',
      marginBottom: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    statusBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
    },
    ratingBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: '#e8eaf6',
      color: '#3f51b5',
      marginLeft: '8px',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#999',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search by name or designation..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
          onFocus={(e) => {
            e.target.style.borderColor = '#e94560';
            e.target.style.boxShadow = '0 0 0 3px rgba(233, 69, 96, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e0e0e0';
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>

      <div style={styles.tabContainer}>
        {Object.entries(categories).map(([key, value]) => (
          <button
            key={key}
            style={{
              ...styles.tab,
              ...(activeTab === key && styles.tabActive),
            }}
            onClick={() => {
              setActiveTab(key);
              setSearchQuery('');
            }}
          >
            {value.label}
          </button>
        ))}
      </div>

      {error ? (
        <div style={styles.emptyState}>
          {error}
        </div>
      ) : loading ? (
        <div style={styles.emptyState}>Loading employees...</div>
      ) : filteredEmployees.length > 0 ? (
        <div style={styles.employeeGrid}>
          {filteredEmployees.map((emp) => (
            <div
              key={emp.id}
              style={{
                ...styles.employeeCard,
                borderTopColor: categories[activeTab]?.color || '#e94560',
              }}
              onClick={() => navigate(`/employee-rating/${emp.id}`)}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, styles.employeeCardHover);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={styles.employeeName}>{emp.name}</div>
              <div style={styles.employeeDesignation}>{emp.designation}</div>

              <div style={styles.infoRow}>
                <span>Category:</span>
                <span style={{ fontWeight: '600', color: '#1a1a2e' }}>
                  {emp.category}
                </span>
              </div>

              <div style={styles.infoRow}>
                <span>Reporting To:</span>
                <span style={{ fontWeight: '600', color: '#1a1a2e' }}>
                  {emp.reportingHead}
                </span>
              </div>

              <div style={{ ...styles.infoRow, marginTop: '12px', marginBottom: '0px' }}>
                <div
                  style={{
                    ...styles.statusBadge,
                    backgroundColor: getStatusColor(emp.status).bg,
                    color: getStatusColor(emp.status).color,
                  }}
                >
                  {emp.status}
                </div>
                {emp.rating && (
                  <div style={styles.ratingBadge}>
                    {emp.rating} ⭐
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.emptyState}>
          No employees found matching your search.
        </div>
      )}
    </div>
  );
};

export default EmployeeList;
